import Product from '@src/quince-site-common/src/domains/Product'
import Bundle from '@src/quince-site-common/src/domains/Product/BundleProduct'

import {
  getInitialSelectedOptions,
  getProductOptionNamesFromProduct,
  isNestedProduct,
} from '@src/quince-components/src/molecules/productCard/productCard.helpers'
import { PriceDetailsMapValue } from '@src/quince-site-common/src/domains/PriceDetailsForSSR'
import { ProductOptionName } from '@src/quince-site-common/src/domains/ProductOption'
import { IProductPurchaseInfo } from '@src/quince-site-common/src/domains/ProductPurchaseInfo'
import { ProductVariantOption } from '@src/quince-site-common/src/domains/ProductVariant'
import productHelper from '@src/quince-site-common/src/helpers/productHelper'
import { getVarientId } from '@src/quince-site-common/src/hooks/cart/hooks/new-cart-helper'
import productService from '@src/quince-site-common/src/services/productService'
import { getOptionBasedOnQueryString } from '../productPurchase/productPurchase.helpers'
import { BUNDLE_PRODUCT_TYPE, BundleConfig, BundleProductConfig } from './BundleProductPurchase.types'

export const getProductsPurchaseInfo = async (products: Product[]) => {
  const purchaseInfoById: Record<number, IProductPurchaseInfo> = {}
  await Promise.all(
    products.map(async product => {
      const purchaseInfo = await productService.getProductPurchaseInfo(product)
      purchaseInfoById[product.id] = purchaseInfo
      return purchaseInfo
    }),
  )
  return purchaseInfoById
}

export const getBundleInitialState = (bundle: Bundle, searchTerm?: string): BundleConfig => {
  const customBundleProducts = bundle.customBundleProducts || []
  const addOnBundleProducts = bundle.addOnBundleProducts || []
  const products: BundleProductConfig[] = []
  const optionalProducts: BundleProductConfig[] = []

  customBundleProducts.forEach(product => {
    products.push(
      getProductInitialState({ product: product, searchTerm: searchTerm, type: BUNDLE_PRODUCT_TYPE.DEFAULT }),
    )
  })

  addOnBundleProducts.forEach(product => {
    optionalProducts.push(
      getProductInitialState({ product: product, searchTerm: searchTerm, type: BUNDLE_PRODUCT_TYPE.OPTIONAL }),
    )
  })

  return { products, optionalProducts }
}

export const getProductInitialState = ({
  product,
  searchTerm,
  type,
}: {
  product: Product
  searchTerm?: string
  type: BUNDLE_PRODUCT_TYPE
}) => {
  const defaultSelectedOptions = []
  if (searchTerm) {
    const colorOption = getOptionBasedOnQueryString(product, 'color', ProductOptionName.Color, searchTerm)
    if (colorOption) {
      defaultSelectedOptions.push(colorOption)
    }
    const productOptionNames = getProductOptionNamesFromProduct(product)
    productOptionNames.forEach(name => {
      const option = getOptionBasedOnQueryString(product, name.toLowerCase(), name as ProductOptionName)
      if (option) {
        defaultSelectedOptions.push(option)
      }
    })
  }

  const initialSelectedOptions = getInitialSelectedOptions(
    product,
    defaultSelectedOptions,
    false,
    false,
    isNestedProduct(product) ? 2 : 1,
  )

  const pricing = productHelper.getProductPriceDetails(product, initialSelectedOptions)

  const variants = productHelper.getVariantsForOptions(product, initialSelectedOptions)
  return {
    type: type,
    product: product,
    selectedOptions: initialSelectedOptions,
    variants: variants,
    purchaseInfo: null,
    salePrice: +pricing.formattedSalePrice,
    traditionalPrice: +(pricing.formattedTraditionalPrice || '0'),
    selection: {
      isSelected: type === BUNDLE_PRODUCT_TYPE.DEFAULT,
    },
    error: null,
  }
}

export function checkSingleVariantSelectedForEachProduct(bundleConfig: BundleConfig): boolean {
  let hasMultipleVariants = false
  ;[...bundleConfig.products, ...bundleConfig.optionalProducts].forEach(config => {
    if (config.selection.isSelected && config.variants.length > 1) {
      hasMultipleVariants = true
    }
  })
  return !hasMultipleVariants
}

export const getAllProductIdsToVariantIdsMapBundle = (bundle: Bundle, bundleMergeProducts: Product[]) => {
  const productIdVsSelectedVariantIdsMap: Record<number, number[]> = {}

  const mergeProducts = bundleMergeProducts || []
  const customBundleProducts = bundle.customBundleProducts || []
  const addOnBundleProducts = bundle.addOnBundleProducts || []

  ;[...mergeProducts, ...customBundleProducts, ...addOnBundleProducts].forEach(product => {
    productIdVsSelectedVariantIdsMap[product.id] = product.variants.map(variant => variant.id)
  })

  return productIdVsSelectedVariantIdsMap
}

export const addPurchaseInfoToProductConfigs = (
  products: BundleProductConfig[],
  purchaseInfo: Record<number, IProductPurchaseInfo>,
) => {
  return products.map(productConfig => {
    const variants = productHelper.getVariantsForOptions(productConfig.product, productConfig.selectedOptions)
    const productPurchaseInfo = purchaseInfo[productConfig.product.id]
    const isOutOfStock =
      variants.length === 1 &&
      (productPurchaseInfo?.shopifyVariantIdVsShopifyVariantInfo[getVarientId(variants[0])]?.quantityAvailable || 0) <=
        0

    return {
      ...productConfig,
      purchaseInfo: productPurchaseInfo,
      error: isOutOfStock ? 'Out of stock' : null,
    }
  })
}

export function getProductPriceMap(
  productsWithSelectedOptions: {
    product: Product
    selectedOptions: ProductVariantOption[]
  }[],
) {
  const productPrices: { product: Product; pricing: PriceDetailsMapValue }[] = []
  productsWithSelectedOptions.forEach(productConfig => {
    productPrices.push({
      product: productConfig.product,
      pricing: productHelper.getProductPriceDetails(productConfig.product, productConfig.selectedOptions),
    })
  })
  return productPrices
}
